/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AutoLoginCheckContainer,
  InputForm,
  Line,
  LoginBtnContainer,
  LoginFormContainer,
  SettingContainer,
  SignupContainer,
} from "./login.styles";
import UI from "@mile-meeting/mile-ui";
import { isValidEmail } from "utils/validation";
import { css } from "@emotion/react";
import { encrypt } from "utils/crypto";
import { googleLogin } from "apis/google";
import { loginWithMile } from "apis/auth/login";
import { useToast } from "hook/useToast";
import AlertModal from "uis/modal/alert-modal";

const Login = () => {
  const location = useLocation();
  const { showToast } = useToast();

  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [autoLogin, setAutoLogin] = useState<boolean>(false);
  const [showPassward, setShowPassword] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [emailHelpText, setEmailHelpText] = useState<string>("");
  const [passwordHelpText, setPasswordHelpText] = useState<string>("");
  const navigate = useNavigate();

  const [showGoogleAlertModal, setShowGoogleAlertModal] = useState<boolean>(false);

  const enableLoginBtn = (email?: string, password?: string) => {
    if (email && isValidEmail(email) && password) return true;
    else return false;
  };

  const onChangeEmail = (value: string) => {
    setEmailError(false);
    setEmailHelpText("");

    if (value.length <= 100) {
      setEmail(value);
    }
  };

  const onChangePassword = (value: string) => {
    setPasswordError(false);
    setPasswordHelpText("");

    if (value.length <= 16) {
      setPassword(value);
    }
  };

  const onHandleFindPassword = () => {
    window.location.href = "find-password";
  };

  const onHandleLogin = async () => {
    if (email && password) {
      const res = await loginWithMile(email, password, autoLogin);
      localStorage.clear();
      sessionStorage.clear();
      if (res?.success === true && res.accessToken) {
        //*자동로그인이 아니라면 -> sessionStorage
        if (!autoLogin) sessionStorage.setItem("access-token", `${encrypt(res.accessToken)}`);
        //*자동로그인이라면 -> localStorage
        else if (autoLogin) localStorage.setItem("access-token", `${encrypt(res.accessToken)}`);

        window.location.href = "/workspace/list";
      } else {
        if (res?.statusCode === 401) {
          setPasswordError(true);
          setPasswordHelpText("비밀번호를 다시 한 번 확인해 주세요.");
        } else if (res?.statusCode === 404) {
          setEmailError(true);
          setEmailHelpText("가입되지 않은 이메일입니다.");
        } else if (res?.statusCode === 409) {
          setShowGoogleAlertModal(true);
        } else {
          setEmailError(true);
          setEmailHelpText("유효한 이메일이 아닙니다. 이메일을 확인해주세요.");
        }
      }
    }
  };

  useEffect(() => {
    if (location.state?.signupSuccess) {
      showToast("회원가입이 성공적으로 완료되었습니다!", "success");
      window.history.replaceState({}, "");
    }
    if (location.state?.passwordSettingSuccess) {
      showToast("비밀번호 재설정이 완료되었습니다.", "success");
      window.history.replaceState({}, "");
    }
    if (location.state?.deleteSuccess) {
      showToast("Mile 회원 탈퇴가 완료되었습니다.", "success");
      window.history.replaceState({}, "");
    }
  }, [location]);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <UI.Navigation.TopNav onClickLogo={() => navigate("/login")} />
      <LoginFormContainer>
        <UI.Typography type="title01" fontStyle="bold" color="var(--alias-color-label-normal)">
          로그인
        </UI.Typography>
        <InputForm>
          <div>
            <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
              이메일
            </UI.Typography>
            <UI.Input
              placeholder="이메일 주소"
              size="medium"
              style={{ marginTop: "4px" }}
              onChange={onChangeEmail}
              value={email}
              error={(!email || (email && isValidEmail(email)) ? false : true) || emailError}
              showHelpText={(!email || (email && isValidEmail(email)) ? false : true) || emailError}
              helpText={emailError ? emailHelpText : "올바른 이메일을 입력해주세요."}
            />
          </div>
          <div
            css={css`
              position: relative;
            `}
          >
            <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
              비밀번호
            </UI.Typography>
            <UI.Input
              onEnter={() => {
                if (enableLoginBtn(email, password)) onHandleLogin();
              }}
              placeholder="비밀번호"
              size="medium"
              style={{ marginTop: "4px" }}
              showSuffixIcon
              suffixIcon={showPassward ? "eyeClosed" : "eye"}
              onChange={onChangePassword}
              type={showPassward ? "text" : "password"}
              value={password}
              error={passwordError}
              showHelpText={passwordHelpText ? true : false}
              helpText={passwordHelpText}
            />
            <div
              onClick={() => setShowPassword(!showPassward)}
              css={css`
                position: absolute;
                width: 20px;
                height: 20px;
                top: 40px;
                right: 14px;
              `}
            ></div>
          </div>
        </InputForm>
        <SettingContainer>
          <AutoLoginCheckContainer>
            <UI.CheckBox selected={autoLogin} onChange={setAutoLogin} />
            <div onClick={() => setAutoLogin(!autoLogin)}>
              <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-alternative)">
                로그인 유지
              </UI.Typography>
            </div>
          </AutoLoginCheckContainer>
          <UI.Button onClick={onHandleFindPassword} size="small" primary={false} title="비밀번호 재설정" type="text" />
        </SettingContainer>
        <LoginBtnContainer>
          <UI.Button onClick={onHandleLogin} size="large" title="로그인" disabled={!enableLoginBtn(email, password)} />
          <Line />
          <UI.Button
            onClick={googleLogin}
            size="large"
            title="구글 계정으로 로그인"
            primary={false}
            type="outlined"
            prefixIcon="google"
            showPrefixIcon
          />
        </LoginBtnContainer>
        <SignupContainer>
          <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-alternative)">
            마일이 처음이시라면
          </UI.Typography>
          <UI.Button title="회원가입" size="medium" type="text" onClick={() => (window.location.href = "/sign-up")} />
        </SignupContainer>
      </LoginFormContainer>
      <AlertModal
        title="구글로 가입된 이메일입니다."
        btnTitle="구글로 로그인하기"
        onChange={googleLogin}
        modalIsOpen={showGoogleAlertModal}
        closeModal={() => setShowGoogleAlertModal(false)}
      />
    </div>
  );
};

export default Login;
